import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Alert, Button, Collapsible, type DropdownOption } from "ui";
import Drawer from "../../../components/Drawer";
import {
  DropdownSearchFormField,
  Form,
  FormDescription,
  FormErrorMessage,
  FormField,
  FormLabel,
  InputFormField,
  useFormContext,
  useFormState,
  useWatch,
} from "../../form";
import {
  createPlanGroup,
  getAccounts,
  getPlans,
  updatePlanGroup,
} from "../helpers/utils";
import { CURRENCIES } from "../../../constants/currencies";
import toast from "react-hot-toast";
import { parseErrorsToObject } from "../../../utils/parseErrors";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { ChevronUpIcon } from "icons/outline";

type PlanGroupDrawerFormProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  planGroupInitialValues: any;
};

export function PlanGroupDrawerForm(props: PlanGroupDrawerFormProps) {
  return (
    <Drawer
      title={props.mode === "add" ? "New Plan Group" : "Edit Plan Group"}
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="max-w-lg"
    >
      <FormContent {...props} />
    </Drawer>
  );
}

const currenciesOptions = CURRENCIES.map((currency) => [
  currency.currency_code,
  currency.currency_code,
]).sort((a, b) => a[1].localeCompare(b[1])) as DropdownOption<string>[];

const FormContent = ({
  mode,
  planGroupInitialValues,
  onClose,
}: PlanGroupDrawerFormProps) => {
  const queryClient = useQueryClient();

  const defaultFormValues = useMemo(() => {
    if (!planGroupInitialValues) return {};
    return {
      name: planGroupInitialValues.name || "",
      currency: planGroupInitialValues.currency || "",
      account: [
        planGroupInitialValues.account.id,
        planGroupInitialValues.account.name,
      ],
      webhook_url: planGroupInitialValues.webhook_url || "",
      successful_redirect_url:
        planGroupInitialValues.successful_redirect_url || "",
      failed_redirect_url: planGroupInitialValues.failed_redirect_url || "",
      time_expired_redirect_url:
        planGroupInitialValues.time_expired_redirect_url || "",
      closed_redirect_url: planGroupInitialValues.closed_redirect_url || "",
      plans: planGroupInitialValues.plans.map((plan: any) => [
        plan.id,
        plan.name,
      ]),
    };
  }, [planGroupInitialValues]);

  const handleSubmit = (data: any) => {
    const formattedData = {
      ...data,
      account: data.account[0],
      plans: data.plans.map((plan: any) => plan[0]),
    };

    const apiCall =
      mode === "add"
        ? createPlanGroup(formattedData)
        : updatePlanGroup(planGroupInitialValues.id, formattedData);

    return apiCall
      .then(() => {
        toast.success(
          `Plan Group ${mode === "add" ? "created" : "updated"} successfully!`,
          {
            id: "plan-group-form",
          }
        );
        onClose();
        queryClient.invalidateQueries({
          queryKey: ["table", "Plan Group"],
        });
      })
      .catch((err) => {
        const errors = parseErrorsToObject(err);
        return { errors };
      });
  };

  return (
    <Form
      defaultValues={defaultFormValues}
      onSubmit={handleSubmit}
      shouldUnregister={false}
      className="contents"
    >
      <Drawer.Content className="space-y-4">
        <FormField name="name" label="Name" required validation="required">
          <FormLabel />
          <InputFormField placeholder="Name" />
          <FormErrorMessage />
        </FormField>

        <FormField
          label="Account"
          name="account"
          required
          validation="required"
          disabled={mode === "edit"}
        >
          <FormLabel />
          <DropdownSearchFormField
            contentProps={{
              className: "z-50",
            }}
            getOptions={(searchTerm) =>
              getAccounts(searchTerm).then(
                (res) =>
                  res.data.data.map(
                    ({
                      columns,
                    }: {
                      columns: { id: string; name: string };
                    }) => [columns.id, columns.name]
                  ) || []
              )
            }
          />
          <FormErrorMessage />
        </FormField>

        <FormField
          label="Currency"
          name="currency"
          required
          validation="required"
          disabled={mode === "edit"}
        >
          <FormLabel />
          <DropdownSearchFormField
            options={currenciesOptions}
            contentProps={{
              className: "z-50",
            }}
          />
          <FormErrorMessage />
        </FormField>

        <PlansFormField />

        <FormField
          name="webhook_url"
          label="Webhook URL"
          required
          validation="required|url"
        >
          <FormLabel />
          <InputFormField placeholder="Webhook URL" />
          <FormDescription>
            A URL that the Moneyhash server will send a POST request to with
            subscription embed status updates.
          </FormDescription>
          {mode === "edit" && (
            <Alert
              state="warning"
              title="Webhook changes apply only to new subscriptions. Existing ones keep using the old URL unless updated manually via API."
              fullWidth
              className="mt-2"
            />
          )}
          <FormErrorMessage />
        </FormField>

        <Collapsible
          defaultOpen
          className="rounded-lg border border-foundations-bold bg-foundations-subtelst-02"
        >
          <Collapsible.Trigger className="group flex w-full items-center justify-between p-4">
            <span>Redirect URLs</span>
            <ChevronUpIcon className="h-5 w-5 text-foundations-subtler transition duration-200 group-data-[panel-open]:rotate-180" />
          </Collapsible.Trigger>

          <Collapsible.Panel>
            <div className="space-y-4 p-4">
              <FormField
                name="successful_redirect_url"
                label="Successful"
                validation="url"
              >
                <FormLabel />
                <InputFormField placeholder="Successful Redirect URL" />
                <FormErrorMessage />
              </FormField>

              <FormField
                name="failed_redirect_url"
                label="Failed"
                validation="url"
              >
                <FormLabel />
                <InputFormField placeholder="Failed Redirect URL" />
                <FormErrorMessage />
              </FormField>

              <FormField
                name="time_expired_redirect_url"
                label="Time Expired"
                validation="url"
              >
                <FormLabel />
                <InputFormField placeholder="Time Expired Redirect URL" />
                <FormErrorMessage />
              </FormField>

              <FormField
                name="closed_redirect_url"
                label="Closed"
                validation="url"
              >
                <FormLabel />
                <InputFormField placeholder="Closed Redirect URL" />
                <FormErrorMessage />
              </FormField>
            </div>
          </Collapsible.Panel>
        </Collapsible>
      </Drawer.Content>

      <Drawer.Footer>
        <FormActions onClose={onClose} mode={mode} />
      </Drawer.Footer>
    </Form>
  );
};

const PlansFormField = () => {
  const { setValue } = useFormContext();
  const currency = useWatch({ name: "currency" });

  useUpdateEffect(() => {
    setValue("plans", []);
  }, [currency]);

  return (
    <FormField
      label="Plans"
      name="plans"
      required
      validation="required"
      disabled={!currency}
    >
      <FormLabel />
      <DropdownSearchFormField
        multiple
        getOptions={(searchTerm) =>
          getPlans({ search: searchTerm, currency }).then(
            (res) =>
              res.data.data.map(
                ({ columns }: { columns: { id: string; name: string } }) => [
                  columns.id,
                  columns.name,
                ]
              ) || []
          )
        }
      />
      <FormErrorMessage />
    </FormField>
  );
};

const FormActions = ({
  mode,
  onClose,
}: Pick<PlanGroupDrawerFormProps, "mode" | "onClose">) => {
  const { isSubmitting } = useFormState();
  return (
    <>
      <Button type="submit" isLoading={isSubmitting}>
        {mode === "add" ? "Create" : "save"}
      </Button>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
    </>
  );
};
